import type { ColunaRecorte, ConsumoRecorte, ItemRecorte } from '../api';

export type TipoItemRecorte = ItemRecorte['tipo'];

export interface ForaDoItem {
  loja: boolean;
  canais: Set<string>;
  pontos: Map<number, Set<ConsumoRecorte>>;
}

function alcancaConsumo(marcados: Set<ConsumoRecorte>, consumo: ConsumoRecorte): boolean {
  return marcados.has('*') || marcados.has(consumo);
}

export function indiceForaPorItem(ocultos: ItemRecorte[]): Map<string, ForaDoItem> {
  const mapa = new Map<string, ForaDoItem>();
  for (const o of ocultos) {
    const chave = `${o.tipo}:${o.idReferencia}`;
    let entrada = mapa.get(chave);
    if (!entrada) {
      entrada = { loja: false, canais: new Set(), pontos: new Map() };
      mapa.set(chave, entrada);
    }
    if (o.idIdentificador !== 0) {
      const consumos = entrada.pontos.get(o.idIdentificador) ?? new Set<ConsumoRecorte>();
      consumos.add(o.consumo ?? '*');
      entrada.pontos.set(o.idIdentificador, consumos);
    } else if (o.canal === '*') entrada.loja = true;
    else entrada.canais.add(o.canal);
  }
  return mapa;
}

export function estaForaNoIndice(
  indice: Map<string, ForaDoItem>,
  tipo: TipoItemRecorte,
  id: number,
  coluna: ColunaRecorte,
): boolean {
  const entrada = indice.get(`${tipo}:${id}`);
  if (!entrada) return false;
  if (entrada.loja || entrada.canais.has(coluna.canal)) return true;
  const consumos = entrada.pontos.get(coluna.idIdentificador);
  return consumos != null && alcancaConsumo(consumos, coluna.consumo);
}

export function foraEm(doItem: ItemRecorte[], coluna: ColunaRecorte): boolean {
  return doItem.some((o) =>
    o.idIdentificador !== 0
      ? o.idIdentificador === coluna.idIdentificador && alcancaConsumo(new Set([o.consumo ?? '*']), coluna.consumo)
      : o.canal === '*' || o.canal === coluna.canal,
  );
}

export function aplicar(
  lista: ItemRecorte[],
  tipo: TipoItemRecorte,
  id: number,
  ligado: boolean,
  colunas: ColunaRecorte[],
  alvos: ColunaRecorte[],
): ItemRecorte[] {
  const doItem = lista.filter((o) => o.tipo === tipo && o.idReferencia === id);
  const fora = new Set(colunas.filter((c) => foraEm(doItem, c)).map((c) => c.chave));
  for (const alvo of alvos) {
    if (ligado) fora.delete(alvo.chave);
    else fora.add(alvo.chave);
  }

  const semEsteItem = lista.filter((o) => !(o.tipo === tipo && o.idReferencia === id));
  if (fora.size === colunas.length && colunas.length > 0) {
    return [...semEsteItem, { tipo, idReferencia: id, canal: '*', idIdentificador: 0, consumo: '*' }];
  }

  const foraDeVerdade = colunas.filter((c) => fora.has(c.chave));
  // Item fora nos DOIS consumos do mesmo totem vira uma linha só: assim ele some
  // também para quem lê o cardápio sem informar consumo (o bot, um PWA antigo).
  const pontosInteiros = new Set(
    foraDeVerdade
      .filter((c) => c.consumo !== '*')
      .map((c) => c.idIdentificador)
      .filter((idPonto) => {
        const doPonto = colunas.filter((c) => c.idIdentificador === idPonto && c.consumo !== '*');
        return doPonto.length > 1 && doPonto.every((c) => fora.has(c.chave));
      }),
  );

  const novas: ItemRecorte[] = [];
  const jaInteiro = new Set<number>();
  for (const c of foraDeVerdade) {
    if (c.consumo !== '*' && pontosInteiros.has(c.idIdentificador)) {
      if (jaInteiro.has(c.idIdentificador)) continue;
      jaInteiro.add(c.idIdentificador);
      novas.push({ tipo, idReferencia: id, canal: c.canal, idIdentificador: c.idIdentificador, consumo: '*' });
      continue;
    }
    novas.push({
      tipo,
      idReferencia: id,
      canal: c.canal,
      idIdentificador: c.idIdentificador,
      consumo: c.consumo,
    });
  }

  return [...semEsteItem, ...novas];
}

/** Ordena para comparar rascunho e original sem depender da ordem das linhas. */
export function normalizar(itens: ItemRecorte[]): string[] {
  return itens
    .map((o) => `${o.tipo}:${o.idReferencia}:${o.canal}:${o.idIdentificador}:${o.consumo ?? '*'}`)
    .sort();
}

/** `GRUPO:12:CARDAPIO_TOTEM:3` -> `GRUPO:12`, para contar item e não linha. */
export function itemDaLinha(linha: string): string {
  const [tipo, id] = linha.split(':');
  return `${tipo}:${id}`;
}

/** Quantos ITENS (não linhas) mudaram entre o que veio do servidor e o rascunho. */
export function contarAlteracoes(iniciais: ItemRecorte[], rascunho: ItemRecorte[]): number {
  const antes = new Set(normalizar(iniciais));
  const depois = new Set(normalizar(rascunho));
  const itens = new Set<string>();
  for (const linha of antes) if (!depois.has(linha)) itens.add(itemDaLinha(linha));
  for (const linha of depois) if (!antes.has(linha)) itens.add(itemDaLinha(linha));
  return itens.size;
}
